import React from "react";
import { colors, fonts, spacings } from "@ui/Theme";
import { AddCircleLine, ExternalLinkLine, InformationLine } from "@ui/IconV2/react";
import Text from "@ui/Text";
import t from "../../../../i18n";
import { css, cx } from "@linaria/core";
import Button from "@ui/Button";
import tui from "@ui/i18n";
import InputV2 from "@ui/Input/InputV2";
import TextareaInput from "@ui/TextareaInput";
import Field from "@ui/Form/Field";
import useInput from "@ui/lib/hooks/useInput";
import { alphaNumRegexp } from "diagnostics-lib/io";
import { nonEmptyStringValidation, nullableNumberValidation } from "../../formValidationFunctions";
import { useQuantities } from "../../../../api/useQuantities";
import { useSetups } from "../../../../api/useSetups";
import { MAX_NAME_LENGTH, MAX_UNIT_LENGTH } from "../../../../domain/parser/quantity/csv/rules";
import ReactSelect from "@ui/ReactSelect";
import { useCanUserDo, useCanUserDoOnProject } from "../../../canUserDo";
import { usePost } from "../../../../api/useCall";
import { useRevalidate } from "../../../../api/useQuery";
import useToasts from "@ui/Toast/useToasts";
import { useEquipmentsInput } from "../useEquipmentInput";
import { useEquipmentTypeInput } from "../useEquipmentTypeInput";
import { useSystemsInput } from "../useSystemInput";
import { getLabel } from "./view/Quantities/useMetadata";
import Checkbox from "@ui/Checkbox";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import { removeLineBreaks } from "diagnostics-lib/string";
import { ActionLink } from "@ui/Link";
import { getHelpCenterUrl } from "../../../../lib/helpCenter";
import type { SidePanelView } from "./SidePanel";
import { FormulaField } from "./FormulaField";
import { DateTime } from "luxon";
import {
  buildFormulaVersionMutations,
  createDraftFormulaVersion,
  deleteDraftFormulaVersion,
  getDuplicateDraftFormulaVersionLocalIds,
  hasFormulaVersionDraftChanges,
  sortDraftFormulaVersions,
  type DraftFormulaVersion,
  updateDraftFormulaVersionFormula,
  updateDraftFormulaVersionStartsAt,
} from "../../../../domain/formula-version";
import { translationLocale } from "../../../../i18n/locale";
import useGroups from "../../../../api/useGroups";
import formatDate from "@ui/i18n/formatDate";

export const MAX_DESCRIPTION_LENGTH = 1000;
interface Props {
  projectId: string;
  timezone: string;
  closePanel: () => void;
  className?: string;
  view: SidePanelView;
  onNameChange?: (name: string | null) => void;
}

export interface CreateFormulaQuantityHandle {
  resetForm: () => void;
}

const CreateFormulaQuantity = React.forwardRef<CreateFormulaQuantityHandle, Props>(
  ({ projectId, timezone, closePanel, className, view, onNameChange }, ref) => {
    const { addToast } = useToasts();
    const quantities = useQuantities(projectId);
    const groups = useGroups(projectId);
    const setups = useSetups(projectId);
    const isUserSetuperOrAbove = useCanUserDoOnProject("PROJECT_SETUP", projectId);
    const canUserModifyGlobalCategories = useCanUserDo("CATEGORY_EDIT");
    const canUserModifyCategories = useCanUserDoOnProject("PROJECT_CATEGORY_EDIT", projectId);

    /** API */
    const createFormula = usePost("/projects/:projectId(uuid)/quantities/formula");
    const revalidate = useRevalidate();

    /** Input */
    const {
      value: inputName,
      onChange: handleInputNameChange,
      dirty: isInputNameDirty,
      setValue: setInputName,
    } = useInput("");
    const {
      value: inputDescription,
      onChange: handleInputDescriptionChange,
      setValue: setInputDescription,
    } = useInput("");
    const {
      value: inputUnit,
      onChange: handleInputUnitChange,
      setValue: setInputUnit,
    } = useInput("");
    const systemInput = useSystemsInput({ systemId: null });
    const equipmentsInput = useEquipmentsInput(projectId, []);
    const equipmentType = useEquipmentTypeInput({
      equipmentTypeId: null,
    });
    const { value: inputMin, onChange: handleInputMinChange, setValue: setInputMin } = useInput("");
    const { value: inputMax, onChange: handleInputMaxChange, setValue: setInputMax } = useInput("");
    const { value: inputFormula, setValue: setInputFormula, dirty: isFormulaDirty } = useInput("");
    const [isFormulaInputChecked, setIsFormulaInputChecked] = React.useState(false);
    const [draftFormulaVersions, setDraftFormulaVersions] = React.useState<DraftFormulaVersion[]>(
      [],
    );

    const handleFormulaInputCheckboxChange = React.useCallback(() => {
      setIsFormulaInputChecked(prev => !prev);
    }, []);

    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const hasDraftFormulaVersionsChanged = React.useMemo(
      () =>
        hasFormulaVersionDraftChanges({
          deletedFormulaVersionIds: [],
          draftFormulaVersions,
          persistedFormulaVersionsById: new Map(),
        }),
      [draftFormulaVersions],
    );
    const visibleFormulaVersions = React.useMemo(
      () => sortDraftFormulaVersions(draftFormulaVersions),
      [draftFormulaVersions],
    );
    const duplicateFormulaVersionLocalIds = React.useMemo(
      () => getDuplicateDraftFormulaVersionLocalIds(visibleFormulaVersions),
      [visibleFormulaVersions],
    );
    const hasDuplicateFormulaVersionStartsAt = duplicateFormulaVersionLocalIds.size > 0;

    const handleAddFormulaVersion = React.useCallback(() => {
      setDraftFormulaVersions(current => [
        createDraftFormulaVersion({ quantityId: "new-quantity" }),
        ...current,
      ]);
    }, []);

    const handleDeleteFormulaVersion = React.useCallback((localId: string) => {
      setDraftFormulaVersions(current => deleteDraftFormulaVersion(current, localId));
    }, []);

    const handleDraftFormulaChange = React.useCallback((localId: string, formula: string) => {
      setDraftFormulaVersions(current =>
        updateDraftFormulaVersionFormula(current, localId, formula),
      );
    }, []);

    const handleDraftStartsAtChange = React.useCallback(
      (localId: string, startsAt: Date | null) => {
        if (startsAt === null) return;
        const normalizedStartsAt = DateTime.fromJSDate(startsAt, {
          zone: timezone,
        })
          .startOf("day")
          .toJSDate();
        setDraftFormulaVersions(current =>
          updateDraftFormulaVersionStartsAt(current, localId, normalizedStartsAt),
        );
      },
      [timezone],
    );
    const cycleOptions = React.useMemo(
      () =>
        groups.map(group => ({
          value: `${group.title ?? "cycle"}-${group.startDate}`,
          name:
            group.title ??
            t("project.settings.quantity.side_panel.section.formula_input.unnamed_cycle"),
          startDateLabel: formatDate(new Date(group.startDate), "slash", timezone),
          startsAt: DateTime.fromMillis(group.startDate, { zone: timezone }).toJSDate(),
        })),
      [groups, timezone],
    );
    const setupOptions = React.useMemo(
      () =>
        setups
          .filter(setup => setup.startDate !== null)
          .map(setup => ({
            value: setup.id,
            name: setup.name,
            startDateLabel: formatDate(setup.startDate ?? "", "slash", timezone),
            startsAt: DateTime.fromISO(setup.startDate ?? "", { zone: timezone }).toJSDate(),
          })),
      [setups, timezone],
    );

    /** Validator */
    const isUnique = React.useCallback(
      (newName: string) => !quantities.find(f => f.name === newName),
      [quantities],
    );

    const [formulaErrorsByVersionId, setFormulaErrorsByVersionId] = React.useState<
      Record<string, string>
    >({});
    const [formulaValidatingByVersionId, setFormulaValidatingByVersionId] = React.useState<
      Record<string, boolean>
    >({});

    const handleFormulaValidationErrorChange = React.useCallback(
      (versionId: string | null, error: string | undefined) => {
        const errorKey = versionId ?? "legacy";
        setFormulaErrorsByVersionId(current => {
          if (error === undefined) {
            if (current[errorKey] === undefined) {
              return current;
            }

            const { [errorKey]: _, ...rest } = current;
            return rest;
          }

          if (current[errorKey] === error) {
            return current;
          }

          return { ...current, [errorKey]: error };
        });
      },
      [],
    );
    const handleFormulaValidationPendingChange = React.useCallback(
      (versionId: string | null, isValidating: boolean | undefined) => {
        const validationKey = versionId ?? "legacy";
        setFormulaValidatingByVersionId(current => {
          if (isValidating === undefined || isValidating === false) {
            if (current[validationKey] === undefined) {
              return current;
            }

            const { [validationKey]: _, ...rest } = current;
            return rest;
          }

          if (current[validationKey] === isValidating) {
            return current;
          }

          return { ...current, [validationKey]: isValidating };
        });
      },
      [],
    );

    /** Error */
    const errorInputName = React.useMemo(() => {
      const emptyError = nonEmptyStringValidation(inputName);
      if (emptyError) return emptyError;
      if (!isUnique(inputName)) return t("errors.conflict");
      if (!inputName.match(alphaNumRegexp)) {
        return t("project.quantities.edit.name.error.format");
      }
      return;
    }, [inputName, isUnique]);

    const errorInputMin = React.useMemo(() => {
      const minIsNumberError = nullableNumberValidation(inputMin);
      if (!minIsNumberError && inputMax) {
        const maxIsNumberError = nullableNumberValidation(inputMax);
        if (!maxIsNumberError && parseFloat(inputMin) >= parseFloat(inputMax)) {
          return t("setup.quantities.edit.min.superior.max.error");
        }
      }
      return minIsNumberError;
    }, [inputMin, inputMax]);
    const errorInputMax = React.useMemo(() => {
      const maxIsNumberError = nullableNumberValidation(inputMax);
      if (!maxIsNumberError && inputMin) {
        const minIsNumberError = nullableNumberValidation(inputMin);
        if (!minIsNumberError && parseFloat(inputMin) >= parseFloat(inputMax)) {
          return t("setup.quantities.edit.max.inferior.min.error");
        }
      }
      return maxIsNumberError;
    }, [inputMin, inputMax]);

    React.useEffect(() => {
      onNameChange?.(inputName.trim() === "" ? null : inputName);
    }, [inputName, onNameChange]);

    const resetForm = React.useCallback(() => {
      setInputName("");
      setInputDescription("");
      setInputUnit("");
      setInputMin("");
      setInputMax("");
      setInputFormula("");
      setInputDescription("");
      setIsFormulaInputChecked(false);
      setDraftFormulaVersions([]);
      setFormulaErrorsByVersionId({});
      setFormulaValidatingByVersionId({});
      systemInput.resetValues();
      equipmentsInput.resetValues();
      equipmentType.resetValues();
    }, [
      setInputName,
      setInputDescription,
      setInputUnit,
      setInputMin,
      setInputMax,
      setInputFormula,
      setIsFormulaInputChecked,
      setDraftFormulaVersions,
      systemInput,
      equipmentsInput,
      equipmentType,
    ]);

    React.useImperativeHandle(ref, () => ({
      resetForm,
    }));

    const handleSubmit = React.useCallback(async () => {
      if (inputName === undefined) throw new Error("Name is required");
      const formulaVersionMutations = buildFormulaVersionMutations({
        deletedFormulaVersionIds: [],
        draftFormulaVersions,
        persistedFormulaVersionsById: new Map(),
      });
      await createFormula({
        body: {
          name: inputName,
          description: removeLineBreaks(inputDescription) ?? null,
          formula: removeLineBreaks(inputFormula),
          locked: isFormulaInputChecked,
          unit: inputUnit ?? null,
          categories: {
            systemId: systemInput.selectedSystem?.value ?? null,
            equipmentTypeId: equipmentType.selectedEquipmentType?.value ?? null,
            equipmentIds: equipmentsInput.selectedEquipments
              .map(e => e.value)
              .filter((id): id is string => id !== null),
          },
          filter: {
            min: inputMin ? parseFloat(inputMin) : null,
            max: inputMax ? parseFloat(inputMax) : null,
            maxStd: null,
          },
          formulaVersions: {
            create: formulaVersionMutations.create,
          },
        },
        routeParams: { projectId },
      });
      await revalidate("/projects/:projectId(uuid)/quantities", { projectId });
      addToast("success", t("projects.quantities.create.quantity.success.toast.title"));
      closePanel();
      resetForm();
    }, [
      inputFormula,
      inputName,
      addToast,
      createFormula,
      inputDescription,
      isFormulaInputChecked,
      inputUnit,
      systemInput.selectedSystem?.value,
      equipmentType.selectedEquipmentType?.value,
      equipmentsInput.selectedEquipments,
      inputMin,
      inputMax,
      draftFormulaVersions,
      projectId,
      revalidate,
      closePanel,
      resetForm,
    ]);

    const hasBaseFormula = (inputFormula ?? "").trim().length > 0;
    const dirty =
      isInputNameDirty && hasBaseFormula && (isFormulaDirty || hasDraftFormulaVersionsChanged);
    const isValid =
      !errorInputName &&
      !errorInputMin &&
      !errorInputMax &&
      Object.keys(formulaErrorsByVersionId).length === 0 &&
      !hasDuplicateFormulaVersionStartsAt;
    const hasValidatingFormula = Object.values(formulaValidatingByVersionId).some(Boolean);
    const formulaHelpUrl = getHelpCenterUrl(
      translationLocale === "fr"
        ? "quantities#expressions-des-grandeurs-calculées"
        : "quantities#calculated-value-expressions",
      translationLocale,
    );

    return (
      <>
        <div className={cx(content, className)}>
          {view === "general-information" && (
            <>
              <section className={content__quantityDefinition}>
                <Text className={sectionTitle}>
                  {t("project.settings.quantity.side_panel.section.definition_quantity.title")}
                </Text>

                <InputV2
                  className={inputFullWidth}
                  label={getLabel("name")}
                  value={inputName}
                  onChange={handleInputNameChange}
                  error={isInputNameDirty ? errorInputName : undefined}
                  dirty={isInputNameDirty}
                  revertable={false}
                  maxLength={MAX_NAME_LENGTH}
                  required
                />

                <Field id="" label={getLabel("description")} className={field}>
                  <TextareaInput
                    value={inputDescription}
                    onChange={handleInputDescriptionChange}
                    maxLength={MAX_DESCRIPTION_LENGTH}
                    minHeight={56}
                    enforceMaxLength
                    showCharCounter={false}
                  />
                </Field>

                <div className={content__quantityDefinition_unitAndSystem}>
                  <InputV2
                    className={unitInput}
                    label={getLabel("unit")}
                    value={inputUnit}
                    onChange={handleInputUnitChange}
                    maxLength={MAX_UNIT_LENGTH}
                  />

                  <Field id="" label={getLabel("system")} className={field}>
                    <ReactSelect
                      menuPortalTarget={window.document.body}
                      placeholder={t("common.select")}
                      value={systemInput.selectedSystem}
                      options={systemInput.systemOptions}
                      onChange={systemInput.handleSystemChanged}
                      onCreate={
                        canUserModifyGlobalCategories ? systemInput.handleSystemCreated : undefined
                      }
                      onEdit={
                        canUserModifyGlobalCategories ? systemInput.handleSystemEdited : undefined
                      }
                      onDelete={
                        canUserModifyGlobalCategories ? systemInput.handleSystemDeleted : undefined
                      }
                    />
                  </Field>
                </div>

                <Field id="" label={getLabel("equipments")} className={field}>
                  <ReactSelect
                    menuPortalTarget={window.document.body}
                    isMulti
                    placeholder={t("common.select")}
                    value={equipmentsInput.selectedEquipments}
                    options={equipmentsInput.equipmentOptions}
                    onChange={equipmentsInput.handleEquipmentChanged}
                    onCreate={
                      canUserModifyCategories ? equipmentsInput.handleEquipmentCreated : undefined
                    }
                    onEdit={
                      canUserModifyCategories ? equipmentsInput.handleEquipmentEdited : undefined
                    }
                    onDelete={
                      canUserModifyCategories ? equipmentsInput.handleEquipmentDeleted : undefined
                    }
                  />
                </Field>

                <Field id="" label={getLabel("equipmentType")} className={field}>
                  <ReactSelect
                    placeholder={t("common.select")}
                    menuPortalTarget={window.document.body}
                    value={equipmentType.selectedEquipmentType}
                    options={equipmentType.equipmentTypeOptions}
                    onChange={equipmentType.handleEquipmentTypeChanged}
                    onCreate={
                      canUserModifyGlobalCategories
                        ? equipmentType.handleEquipmentTypeCreated
                        : undefined
                    }
                    onEdit={
                      canUserModifyGlobalCategories
                        ? equipmentType.handleEquipmentTypeEdited
                        : undefined
                    }
                    onDelete={
                      canUserModifyGlobalCategories
                        ? equipmentType.handleEquipmentTypeDeleted
                        : undefined
                    }
                  />
                </Field>
              </section>

              <section className={content__metrological}>
                <Text className={sectionTitle}>
                  {t("project.settings.quantity.side_panel.section.metrological_filters.title")}
                </Text>

                <div className={content__metrological_minAndMax}>
                  <InputV2
                    className={inputFullWidth}
                    label={getLabel("min")}
                    type="number"
                    step={1}
                    value={inputMin}
                    onChange={handleInputMinChange}
                    error={errorInputMin}
                  />
                  <InputV2
                    className={inputFullWidth}
                    label={getLabel("max")}
                    type="number"
                    step={1}
                    value={inputMax}
                    onChange={handleInputMaxChange}
                    error={errorInputMax}
                  />
                </div>
              </section>
            </>
          )}

          {view === "formulae" && (
            <section className={content__formula}>
              <ActionLink
                className={formulaHelpLink}
                href={formulaHelpUrl}
                target="_blank"
                rel="noreferrer"
              >
                <ExternalLinkLine />
                {t("project.settings.quantity.side_panel.section.formula_input.help_link")}
              </ActionLink>
              <Button
                style={{ width: "max-content" }}
                variant="link"
                leftIcon={<AddCircleLine />}
                onClick={handleAddFormulaVersion}
              >
                {t("project.settings.quantity.side_panel.tab.formulae.add")}
              </Button>
              <Text className={sectionTitle}>
                {t("project.settings.quantity.side_panel.section.formula_input.title")}
              </Text>
              {isUserSetuperOrAbove && (
                <Checkbox
                  checked={isFormulaInputChecked}
                  onChange={handleFormulaInputCheckboxChange}
                >
                  <span className={checkboxSpan}>
                    {t("project.settings.quantity.side_panel.section.formula_input.lock_title")}
                    <TooltipV2
                      overlay={
                        <OverlayWrapper
                          className={styledOverlayWrapper}
                          text={t(
                            "project.settings.quantity.side_panel.section.formula_input.lock_tooltip",
                          )}
                        />
                      }
                    >
                      <span className={tooltipSpan}>
                        <InformationLine size="m" color={colors.carbon800} />
                      </span>
                    </TooltipV2>
                  </span>
                </Checkbox>
              )}
              {visibleFormulaVersions.map((fv, i, arr) => (
                <FormulaField
                  versionId={fv.localId}
                  key={fv.localId}
                  projectId={projectId}
                  index={arr.length - i + 1}
                  formula={fv.formula}
                  startsAt={fv.startsAt}
                  endsAt={arr[i - 1]?.startsAt ?? null}
                  quantityId={null}
                  timezone={timezone}
                  onFormulaChange={handleDraftFormulaChange}
                  onStartsAtChange={handleDraftStartsAtChange}
                  onStartsAtFillFromCycle={handleDraftStartsAtChange}
                  onStartsAtFillFromSetup={handleDraftStartsAtChange}
                  onDelete={handleDeleteFormulaVersion}
                  cycleOptions={cycleOptions}
                  setupOptions={setupOptions}
                  onValidationErrorChange={handleFormulaValidationErrorChange}
                  onValidationPendingChange={handleFormulaValidationPendingChange}
                  startsAtError={
                    duplicateFormulaVersionLocalIds.has(fv.localId)
                      ? t(
                          "project.settings.quantity.side_panel.section.formula_input.duplicate_usage_period_error",
                        )
                      : undefined
                  }
                  required
                />
              ))}
              <FormulaField
                versionId={null}
                legacyFormula
                projectId={projectId}
                index={1}
                formula={inputFormula}
                startsAt={null}
                endsAt={visibleFormulaVersions.at(-1)?.startsAt ?? null}
                quantityId={null}
                timezone={timezone}
                onFormulaChange={(_, formula) => setInputFormula(formula)}
                onValidationErrorChange={handleFormulaValidationErrorChange}
                onValidationPendingChange={handleFormulaValidationPendingChange}
                required
              />
            </section>
          )}
        </div>
        <div className={submit}>
          <div className={submit__mandatory}>
            <sup className={submit__sup}>*</sup> {tui("input.mandatory")}
          </div>
          <div className={submit__action}>
            <Button
              onClick={() => {
                closePanel();
                resetForm();
              }}
            >
              {t("commons.message_card.cancel")}
            </Button>
            <Button
              variant="primary"
              disabled={!dirty || !isValid || isSubmitting || hasValidatingFormula}
              onClick={async () => {
                setIsSubmitting(true);
                try {
                  await handleSubmit();
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              {t("commons.button.activate")}
            </Button>
          </div>
        </div>
      </>
    );
  },
);

export default CreateFormulaQuantity;

//#region CSS
export const content = css`
  flex: 1;

  padding: ${spacings.m};
  display: flex;
  flex-direction: column;
  gap: ${spacings.xl};
  width: 100%;
`;

export const content__quantityDefinition = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.m};
`;
export const content__quantityDefinition_unitAndSystem = css`
  display: flex;
  gap: ${spacings.m};
  & > :nth-child(1) {
    width: 100px;
  }
`;
export const content__quantityDefinition_equipmentAndTypeEquipment = css`
  display: flex;
  gap: ${spacings.m};
`;

export const content__metrological = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.m};
`;
export const content__metrological_minAndMax = css`
  display: flex;
  gap: ${spacings.m};
`;

export const content__formula = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.m};
`;

export const formulaHelpLink = css`
  display: flex;
  align-items: center;
  column-gap: ${spacings.xxs};
  align-self: flex-start;
`;

export const inputFullWidth = css`
  width: 100%;
`;

export const unitInput = css`
  > div {
    height: 38px;
  }
  border-color: ${colors.titanium200};
`;

export const field = css`
  width: 100%;

  > div {
    border: unset;
    padding-left: 0;
    width: 100%;

    > div {
      width: 100%;

      div[class*="control"] {
        border-color: ${colors.titanium200};
      }
    }
  }
`;

export const submit = css`
  position: sticky;
  bottom: 0;
  padding: ${spacings.m};
  display: flex;
  justify-content: space-between;
  border-top: 1px solid ${colors.titanium200};
  background-color: ${colors.titanium50};
`;
export const submit__sup = css`
  color: ${colors.darkNitrogen600};
  vertical-align: bottom;
`;
export const submit__mandatory = css`
  align-self: flex-end;
  font: ${fonts.body3};
  color: ${colors.darkNitrogen600};
`;
export const submit__action = css`
  * + * {
    margin-left: ${spacings.xs};
  }
`;

export const tooltipSpan = css`
  outline: none;
  display: flex;
  align-items: center;
`;

export const checkboxSpan = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xxs};
`;

const styledOverlayWrapper = css`
  max-width: 200px;
`;

export const sectionTitle = css`
  font: ${fonts.body1SemiBold};
`;
//#endregion CSS
